import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Download,
  FileText,
  Database,
  Loader2,
  Check,
  Clock,
  AlertCircle,
  Info,
  FileJson,
  FileSpreadsheet,
  RefreshCw
} from 'lucide-react'
import toast from 'react-hot-toast'
import { createLogger } from '../../utils/logger'

const logger = createLogger('DataExport')

const DataExport = () => {
  const [format, setFormat] = useState('json')
  const [isRequesting, setIsRequesting] = useState(false)
  const [exportStatus, setExportStatus] = useState(null)
  const [isCheckingStatus, setIsCheckingStatus] = useState(true)

  useEffect(() => {
    // Check for existing export on mount
    checkExportStatus()
  }, [])

  useEffect(() => {
    // Auto-refresh status every 30 seconds if export is pending or processing
    if (exportStatus && ['pending', 'processing'].includes(exportStatus.status)) {
      const interval = setInterval(() => {
        checkExportStatus()
      }, 30000) // 30 seconds

      return () => clearInterval(interval)
    }
  }, [exportStatus])

  const checkExportStatus = async () => {
    setIsCheckingStatus(true)
    try {
      // Get the latest export ID from the API or localStorage
      const savedExportId = localStorage.getItem('lastExportId')
      if (!savedExportId) {
        setIsCheckingStatus(false)
        return
      }

      const response = await fetch(`/api/compliance/export-status/${savedExportId}`)
      const data = await response.json()

      if (response.ok && data.success && data.data) {
        // Only show status if it's pending, processing, or recently completed
        if (['pending', 'processing', 'completed'].includes(data.data.status)) {
          setExportStatus(data.data)
        } else {
          // Clear old exports
          localStorage.removeItem('lastExportId')
        }
      }
    } catch (error) {
      logger.error('Error checking export status:', error)
    } finally {
      setIsCheckingStatus(false)
    }
  }

  const handleRequestExport = async () => {
    setIsRequesting(true)
    try {
      const response = await fetch('/api/compliance/export-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ format })
      })

      const data = await response.json()

      if (!response.ok) {
        if (response.status === 409) {
          toast.error('You already have a pending export. Please wait for it to complete.')
          // Refresh status
          await checkExportStatus()
          return
        }
        throw new Error(data.error || data.message || 'Failed to request data export')
      }

      if (data.success) {
        const exportData = data.data || data
        const exportId = exportData.exportId || exportData.id

        // Set initial status
        setExportStatus({
          id: exportId,
          status: 'pending',
          format,
          createdAt: new Date().toISOString()
        })

        localStorage.setItem('lastExportId', exportId)
        toast.success(
          "Data export requested! Processing will begin shortly. You'll receive an email when ready."
        )
      }
    } catch (error) {
      logger.error('Error requesting export:', error)
      toast.error(error.message || 'Failed to request data export')
    } finally {
      setIsRequesting(false)
    }
  }

  const handleDownload = async () => {
    if (!exportStatus?.id) {
      toast.error('Export ID not found')
      return
    }

    if (exportStatus.status !== 'completed') {
      toast.error('Export is not ready yet')
      return
    }

    try {
      // Trigger download
      window.location.href = `/api/compliance/download-export/${exportStatus.id}`
      toast.success('Download started!')
    } catch (error) {
      logger.error('Download error:', error)
      toast.error('Failed to download export')
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400 animate-pulse" />
      case 'processing':
        return <Loader2 className="w-5 h-5 text-primary-600 dark:text-primary-400 animate-spin" />
      case 'completed':
        return <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
      default:
        return <Database className="w-5 h-5 text-gray-600 dark:text-gray-400" />
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Your request is being processed...'
      case 'processing':
        return 'Collecting and preparing your data...'
      case 'completed':
        return 'Your data export is ready!'
      case 'failed':
        return 'Export failed. Please try again.'
      default:
        return 'Unknown status'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
      case 'processing':
        return 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800'
      case 'completed':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
      case 'failed':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
      default:
        return 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800'
    }
  }

  const getDaysUntilExpiration = (expiresAt) => {
    if (!expiresAt) return null
    const now = new Date()
    const expires = new Date(expiresAt)
    const diffTime = expires - now
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <Download className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Export Your Data</h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Download a copy of your personal data (GDPR Article 20)
        </p>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Existing Export Status */}
        <AnimatePresence>
          {exportStatus && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`p-4 rounded-lg border ${getStatusColor(exportStatus.status)}`}
            >
              <div className="flex items-start gap-3">
                {getStatusIcon(exportStatus.status)}
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    {getStatusText(exportStatus.status)}
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Format: {exportStatus.format?.toUpperCase()} • Requested:{' '}
                    {new Date(exportStatus.createdAt).toLocaleString()}
                  </p>
                  {exportStatus.status === 'completed' && exportStatus.completedAt && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Completed: {new Date(exportStatus.completedAt).toLocaleString()}
                    </p>
                  )}
                  {exportStatus.status === 'completed' && exportStatus.expiresAt && (
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Expires in {getDaysUntilExpiration(exportStatus.expiresAt)} day(s)
                    </p>
                  )}
                  {exportStatus.status === 'completed' && exportStatus.fileSize && (
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Size: {(exportStatus.fileSize / 1024).toFixed(2)} KB
                    </p>
                  )}
                  {exportStatus.status === 'failed' && exportStatus.errorMessage && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      Error: {exportStatus.errorMessage}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {['pending', 'processing'].includes(exportStatus.status) && (
                    <button
                      onClick={() => checkExportStatus()}
                      disabled={isCheckingStatus}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 disabled:opacity-50"
                      title="Refresh status"
                    >
                      <RefreshCw className={`w-4 h-4 ${isCheckingStatus ? 'animate-spin' : ''}`} />
                    </button>
                  )}
                  {exportStatus.status === 'completed' && (
                    <button
                      onClick={handleDownload}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download</span>
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Export Request Form */}
        {(!exportStatus || exportStatus.status === 'failed') && (
          <>
            {/* Format Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Export Format
              </label>
              <div className="grid grid-cols-2 gap-4">
                {/* JSON Option */}
                <label
                  className={`relative flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    format === 'json'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="format"
                    value="json"
                    checked={format === 'json'}
                    onChange={(e) => setFormat(e.target.value)}
                    className="sr-only"
                  />
                  <FileJson
                    className={`w-6 h-6 ${format === 'json' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">JSON</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Machine-readable</div>
                  </div>
                  {format === 'json' && (
                    <Check className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  )}
                </label>

                {/* CSV Option */}
                <label
                  className={`relative flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    format === 'csv'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="format"
                    value="csv"
                    checked={format === 'csv'}
                    onChange={(e) => setFormat(e.target.value)}
                    className="sr-only"
                  />
                  <FileSpreadsheet
                    className={`w-6 h-6 ${format === 'csv' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">CSV</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Spreadsheet format
                    </div>
                  </div>
                  {format === 'csv' && (
                    <Check className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  )}
                </label>
              </div>
            </div>

            {/* Info Notice */}
            <div className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <Info className="w-4 h-4 text-gray-600 dark:text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-gray-600 dark:text-gray-400">
                <p className="font-semibold mb-1">What's included:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Profile information and preferences</li>
                  <li>Challenge responses and performance data</li>
                  <li>Progress and skill level history</li>
                  <li>Feedback submissions</li>
                </ul>
                <p className="mt-2">
                  Your export will be available for download for 7 days. You can only have one
                  pending export at a time.
                </p>
              </div>
            </div>

            {/* Request Button */}
            <button
              onClick={handleRequestExport}
              disabled={isRequesting || isCheckingStatus}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              {isRequesting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Requesting Export...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span>Request Data Export</span>
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default DataExport
